import { useUserContext } from "@Hooks/contexts/useUserContext";
import { styled } from "@mui/material/styles";
import { Button, DocumentIcon, EmptyState, ErrorIcon, FolderIcon, getTopSelectedItems, LinkIcon, Meta, SearchField, SearchIcon, Sheet, Spinner, TreeList } from "@UI";
import { getIconFromType } from "@Utils/tree/getIconFromType";
import React from "react";
import { useTranslation } from "react-i18next";
import { fetchAvailableConnections } from "../api/chatApi";
import { useChatComposerStore } from "../store/chatComposerStore";
export const connectionKey = (connection)=>`${connection.type}-${connection.id}`;
const Footer = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)"
});
const Count = styled(Meta)({
    flex: 1,
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap"
});
const Results = styled("div")({
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    margin: "var(--j-sp-2) calc(-1 * var(--j-sp-3)) 0",
    padding: "0 var(--j-sp-3) var(--j-sp-1)",
    display: "flex",
    flexDirection: "column"
});
function collectIds(items, into = []) {
    items.forEach((item)=>{
        into.push(item.itemId);
        collectIds(item.children ?? [], into);
    });
    return into;
}
function withDescendants(items, seeds) {
    const out = [];
    const visit = (item, isCovered)=>{
        const isSelected = isCovered || seeds.has(item.itemId);
        if (isSelected) out.push(item.itemId);
        (item.children ?? []).forEach((child)=>visit(child, isSelected));
    };
    items.forEach((item)=>visit(item, false));
    return out;
}
function ancestorsOf(items, itemId, path = []) {
    for(let i = 0; i < items.length; i++){
        if (items[i].itemId === itemId) return path;
        const found = ancestorsOf(items[i].children ?? [], itemId, [
            ...path,
            items[i].itemId
        ]);
        if (found) return found;
    }
    return null;
}
function findItem(items, itemId) {
    for(let i = 0; i < items.length; i++){
        if (items[i].itemId === itemId) return items[i];
        const found = findItem(items[i].children ?? [], itemId);
        if (found) return found;
    }
    return null;
}
function translateFolders(items, t) {
    return items.map((item)=>({
            ...item,
            title: item.isAbstract ? t(`workflows.folders.${item.type}`, {
                defaultValue: item.title
            }) : item.title,
            children: item.children && translateFolders(item.children, t)
        }));
}
function filterTree(items, query) {
    return items.reduce((kept, item)=>{
        if (item.title.toLowerCase().includes(query)) return [
            ...kept,
            item
        ];
        const children = filterTree(item.children ?? [], query);
        return children.length ? [
            ...kept,
            {
                ...item,
                children
            }
        ] : kept;
    }, []);
}
export function ConnectionsPicker({ open, onClose }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s.user?.meta?.access_token?.token);
    const setConnections = useChatComposerStore((s)=>s.setConnections);
    const [items, setItems] = React.useState([]);
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [attempt, setAttempt] = React.useState(0);
    const [searchExpansion, setSearchExpansion] = React.useState({
        query: "",
        ids: []
    });
    React.useEffect(()=>{
        if (!open) return undefined;
        let isCancelled = false;
        setIsLoading(true);
        setHasError(false);
        setQuery("");
        const seeds = new Set(useChatComposerStore.getState().connections.map(connectionKey));
        setSelectedItems([
            ...seeds
        ]);
        fetchAvailableConnections(token).then((fetched)=>{
            if (isCancelled) return;
            const tree = translateFolders(fetched, t);
            setItems(tree);
            setSelectedItems(withDescendants(tree, seeds));
        }).catch(()=>{
            if (!isCancelled) setHasError(true);
        }).finally(()=>{
            if (!isCancelled) setIsLoading(false);
        });
        return ()=>{
            isCancelled = true;
        };
    }, [
        open,
        token,
        attempt,
        t
    ]);
    const trimmed = query.trim().toLowerCase();
    const visibleItems = React.useMemo(()=>trimmed ? filterTree(items, trimmed) : items, [
        items,
        trimmed
    ]);
    const searchExpanded = React.useMemo(()=>trimmed ? collectIds(visibleItems) : undefined, [
        trimmed,
        visibleItems
    ]);
    const expandedItems = searchExpanded && (searchExpansion.query === trimmed ? searchExpansion.ids : searchExpanded);
    const hasRows = visibleItems.some((item)=>item.children?.length);
    const topSelected = getTopSelectedItems(items, selectedItems);
    const onSelect = (next, toggled)=>{
        const item = trimmed ? findItem(items, toggled.itemId) : null;
        const wasSelected = item ? selectedItems.includes(item.itemId) : false;
        if (!item || wasSelected === next.includes(item.itemId)) {
            setSelectedItems(next);
            return;
        }
        const affected = [
            item.itemId,
            ...collectIds(item.children ?? [])
        ];
        const result = new Set(selectedItems);
        if (wasSelected) [
            ...affected,
            ...ancestorsOf(items, item.itemId) ?? []
        ].forEach((id)=>result.delete(id));
        else affected.forEach((id)=>result.add(id));
        setSelectedItems([
            ...result
        ]);
    };
    const apply = ()=>{
        setConnections(topSelected.map((item)=>({
                id: item.id,
                type: item.type,
                title: item.title,
                isAbstract: item.isAbstract
            })));
        onClose();
    };
    const body = isLoading ? /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(Spinner, {
            size: "lg"
        }),
        tone: "neutral",
        text: t("connections.fetching")
    }) : hasError ? /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
        tone: "neutral",
        text: t("chat.connections.loadFailed"),
        actions: /*#__PURE__*/ React.createElement(Button, {
            variant: "tonal",
            size: "lg",
            onClick: ()=>setAttempt((count)=>count + 1)
        }, t("chat.retry"))
    }) : !hasRows ? trimmed ? /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(SearchIcon, null),
        tone: "neutral",
        title: t("chat.connections.noMatches")
    }) : /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(LinkIcon, null),
        tone: "neutral",
        title: t("connections.none")
    }) : /*#__PURE__*/ React.createElement(TreeList, {
        items: visibleItems,
        "aria-label": t("chat.connections.title"),
        selectionMode: "multiple",
        selectedItems: selectedItems,
        onSelectedItemsChange: onSelect,
        expandedItems: expandedItems,
        onExpandedItemsChange: trimmed ? (ids)=>setSearchExpansion({
                query: trimmed,
                ids
            }) : undefined,
        showRootsAsSections: true,
        getItemIcon: (item, { isParent })=>{
            const Icon = getIconFromType(item.type, item.isAbstract) ?? (isParent ? FolderIcon : DocumentIcon);
            return /*#__PURE__*/ React.createElement(Icon, {
                size: 16
            });
        }
    });
    return /*#__PURE__*/ React.createElement(Sheet, {
        open: open,
        onClose: onClose,
        fullHeight: true,
        title: t("chat.connections.title"),
        footer: /*#__PURE__*/ React.createElement(Footer, null, /*#__PURE__*/ React.createElement(Count, null, t("chat.connections.selected", {
            count: topSelected.length
        })), /*#__PURE__*/ React.createElement(Button, {
            variant: "outline",
            size: "lg",
            onClick: onClose
        }, t("chat.connections.cancel")), /*#__PURE__*/ React.createElement(Button, {
            size: "lg",
            onClick: apply,
            disabled: isLoading || hasError
        }, topSelected.length ? t("chat.connections.attachCount", {
            count: topSelected.length
        }) : t("chat.connections.attach")))
    }, /*#__PURE__*/ React.createElement(SearchField, {
        value: query,
        onChange: setQuery,
        placeholder: t("chat.connections.search")
    }), /*#__PURE__*/ React.createElement(Results, null, body));
}
